import React, { useEffect, useId } from 'react';
import { Info, X, SlidersHorizontal, Gauge as GaugeIcon, Map as MapIcon, Activity, AlertCircle, AlertTriangle, BarChart3, BatteryCharging, BatteryLow, BatteryWarning, Bell, BellRing, BookOpen, Bug, CheckCircle2, Circle, Clock, Cloud, CloudDrizzle, CloudFog, CloudLightning, CloudRain, Compass, Cpu, Database, Droplets, Eye, Flame, FlaskConical, Grid3x3, Inbox, Keyboard, LayoutDashboard, Leaf, LifeBuoy, Lightbulb, LineChart, Link2, LogOut, MapPinOff, HelpCircle, MonitorSmartphone, Moon, Palette, Radio, RadioTower, Router, ScatterChart, SearchX, Send, Server, Settings, ShieldCheck, Signal, Snowflake, Sprout, Sun, Table2, Thermometer, ThermometerSun, TrendingUp, User, Users, WifiOff, Wind, Workflow, XOctagon } from 'lucide-react';

import { STATUS, METRICS } from '../../utils/metrics';
import { cx, fmt, ratio, clamp } from '../../utils/format';
import { usePrefs } from '../../context/PrefsContext';

const Icons = { SlidersHorizontal, Gauge: GaugeIcon, Map: MapIcon, Activity, AlertCircle, AlertTriangle, BarChart3, BatteryCharging, BatteryLow, BatteryWarning, Bell, BellRing, BookOpen, Bug, CheckCircle2, Circle, Clock, Cloud, CloudDrizzle, CloudFog, CloudLightning, CloudRain, Compass, Cpu, Database, Droplets, Eye, Flame, FlaskConical, Grid3x3, Inbox, Keyboard, LayoutDashboard, Leaf, LifeBuoy, Lightbulb, LineChart, Link2, LogOut, MapPinOff, HelpCircle, MonitorSmartphone, Moon, Palette, Radio, RadioTower, Router, ScatterChart, SearchX, Send, Server, Settings, ShieldCheck, Signal, Snowflake, Sprout, Sun, Table2, Thermometer, ThermometerSun, TrendingUp, User, Users, WifiOff, Wind, Workflow, XOctagon };

export const Icon = ({ name, size = 18, ...p }) => {
  const C = Icons[name] || Icons.Circle;
  return <C size={size} strokeWidth={1.75} {...p} />;
};

// small "i" bubble that explains a number or a term on hover / focus
export function InfoTip({ text, size = 14 }) {
  const id = useId();
  return (
    <span className="infotip" tabIndex={0} aria-describedby={id}>
      <Info size={size} />
      <span className="infotip__pop" role="tooltip" id={id}>{text}</span>
    </span>
  );
}

export function StatusBadge({ status, label }) {
  const s = STATUS[status] || STATUS.offline;
  return (
    <span className={cx('badge', s.tone)} title={s.help}>
      <i className={cx('dot', status === 'online' && 'pulse')} />{label || s.label}
    </span>
  );
}

export const SeverityBadge = ({ severity }) => (
  <span className={cx('badge', severity === 'critical' ? 'crit' : severity === 'warning' ? 'warn' : 'info')}>{severity}</span>
);

export function Card({ title, sub, icon, actions, children, className, bodyClass, flush, tip, ...rest }) {
  return (
    <section className={cx('card', className)} {...rest}>
      {(title || actions) && (
        <header className="card__head">
          <div>
            <h3 className="card__title">{icon && <Icon name={icon} size={17} />}{title}{tip && <InfoTip text={tip} />}</h3>
            {sub && <div className="card__sub">{sub}</div>}
          </div>
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      <div className={cx('card__body', flush && 'flush', bodyClass)}>{children}</div>
    </section>
  );
}

export function Kpi({ label, value, unit, icon, meta, tip, tone, loading, ...rest }) {
  return (
    <div className="card kpi fade-up" {...rest}>
      <div className="kpi__icon" style={tone ? { background: `var(--${tone}-soft)`, color: `var(--${tone})` } : undefined}><Icon name={icon} size={19} /></div>
      <div className="kpi__label">{label}{tip && <InfoTip text={tip} />}</div>
      {loading ? <div className="skeleton" style={{ height: 34, width: 110, marginTop: 8 }} /> : <div className="kpi__value">{value}{unit && <small>{unit}</small>}</div>}
      {meta && <div className="kpi__meta">{meta}</div>}
    </div>
  );
}

export const Skeleton = ({ h = 16, w = '100%', style }) => <div className="skeleton" style={{ height: h, width: w, ...style }} />;

export function Empty({ icon = 'Inbox', title, children, action }) {
  return (
    <div className="empty">
      <Icon name={icon} size={34} />
      <b>{title}</b>
      {children && <div style={{ maxWidth: 380 }}>{children}</div>}
      {action}
    </div>
  );
}

export const ErrorBox = ({ error, onRetry }) => (
  <div className="empty">
    <AlertCircle size={34} color="var(--crit)" />
    <b>Something went wrong</b>
    <div>{error?.message || 'Unable to load data.'}</div>
    {onRetry && <button className="btn" onClick={onRetry}>Try again</button>}
  </div>
);

export function PageHead({ title, sub, children }) {
  return (
    <div className="page__head">
      <div>
        <h1>{title}</h1>
        {sub && <p className="page__sub">{sub}</p>}
      </div>
      {children && <div className="row wrap">{children}</div>}
    </div>
  );
}

export function Segmented({ value, onChange, options }) {
  return (
    <div className="seg" role="tablist">
      {options.map((o) => {
        const v = o.value ?? o;
        return <button key={v} role="tab" aria-selected={value === v} className={value === v ? 'on' : ''} onClick={() => onChange(v)}>{o.label ?? o}</button>;
      })}
    </div>
  );
}

export function Switch({ on, onChange, label }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className={cx('switch', on && 'on')} onClick={() => onChange(!on)} />;
}

/* ---------- Sparkline (pure SVG, cheap enough for dozens of cards) ---------- */
export function Spark({ data = [], w = 120, h = 34, color = '#16a34a', fill = true }) {
  const vals = data.map((d) => (typeof d === 'number' ? d : d.v)).filter((v) => v != null);
  const gid = useId();
  if (vals.length < 2) return <svg width={w} height={h} />;
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const pts = vals.map((v, i) => [(i / (vals.length - 1)) * (w - 4) + 2, h - 3 - ((v - min) / span) * (h - 8)]);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={color} stopOpacity=".28" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      {fill && <path d={`${line} L${w - 2},${h} L2,${h} Z`} fill={`url(#${gid})`} />}
      <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill={color} />
    </svg>
  );
}

/* ---------- semi-circular gauge with the "safe" band drawn on it ---------- */
const polar = (cx, cy, r, a) => [cx + r * Math.cos(a), cy - r * Math.sin(a)];
const arc = (cx, cy, r, a0, a1) => {
  const [x0, y0] = polar(cx, cy, r, a0);
  const [x1, y1] = polar(cx, cy, r, a1);
  return `M${x0.toFixed(2)},${y0.toFixed(2)} A${r},${r} 0 ${a0 - a1 > Math.PI ? 1 : 0} 1 ${x1.toFixed(2)},${y1.toFixed(2)}`;
};
export function Gauge({ value, min, max, lo, hi, unit, color = '#16a34a', decimals = 1, size = 190 }) {
  const cx = 100, cy = 92, r = 74;
  const ang = (v) => Math.PI * (1 - clamp((v - min) / (max - min), 0, 1));
  const has = lo != null && hi != null;
  const a = value == null ? Math.PI : ang(value);
  const [nx, ny] = polar(cx, cy, r - 14, a);
  const out = has && value != null && (value < lo || value > hi);
  const c = out ? 'var(--crit)' : color;
  return (
    <svg viewBox="0 0 200 138" width={size} height={size * 0.69} role="img" aria-label={`value ${value} ${unit}`}>
      <path d={arc(cx, cy, r, Math.PI, 0)} stroke="var(--bg-sunken)" strokeWidth="13" fill="none" strokeLinecap="round" />
      {has && <>
        <path d={arc(cx, cy, r, Math.PI, ang(lo))} stroke="var(--warn)" strokeOpacity=".5" strokeWidth="13" fill="none" strokeLinecap="round" />
        <path d={arc(cx, cy, r, ang(hi), 0)} stroke="var(--warn)" strokeOpacity=".5" strokeWidth="13" fill="none" strokeLinecap="round" />
        <path d={arc(cx, cy, r, ang(lo), ang(hi))} stroke="var(--ok)" strokeOpacity=".55" strokeWidth="13" fill="none" />
      </>}
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={c} strokeWidth="3.5" strokeLinecap="round" style={{ transition: 'all .6s' }} />
      <circle cx={cx} cy={cy} r="6.5" fill={c} />
      <text x="100" y="121" textAnchor="middle" fontSize="22" fontWeight="800" fill="var(--text)" style={{ fontVariantNumeric: 'tabular-nums' }}>{value == null ? '--' : value.toFixed(decimals)}</text>
      <text x="100" y="134" textAnchor="middle" fontSize="9.5" fill="var(--text-3)">{unit}</text>
      <text x="26" y="108" fontSize="9" fill="var(--text-3)" textAnchor="middle">{min}</text>
      <text x="174" y="108" fontSize="9" fill="var(--text-3)" textAnchor="middle">{max}</text>
    </svg>
  );
}

/* ---------- current value of one measurement with its limits and 24 h range ---------- */
export function MetricTile({ metric, value, lo, hi, stats, flash }) {
  const { units } = usePrefs();
  const m = METRICS[metric];
  const hasLimits = lo != null && hi != null;
  const out = hasLimits && value != null && (value < lo || value > hi);
  const pos = hasLimits && value != null ? clamp(ratio(value, lo - (hi - lo) * 0.25, hi + (hi - lo) * 0.25), 0, 1) : null;
  return (
    <div className="card metric-tile" style={{ padding: 14 }}>
      <div className="row between">
        <span className="row" style={{ gap: 7, color: m.color, fontWeight: 700, fontSize: 12.5 }}><Icon name={m.icon} size={16} />{m.short}</span>
        <InfoTip text={m.help} />
      </div>
      <div className={cx('kpi__value', flash && 'flash-text')} style={{ marginTop: 8, color: out ? 'var(--crit)' : undefined }}>
        {fmt(metric, value, units)}<small>{m.unit === '°C' && units === 'imperial' ? '°F' : m.unit}</small>
      </div>
      {hasLimits && (
        <div style={{ marginTop: 10 }}>
          <div className="meter" style={{ background: 'linear-gradient(90deg, var(--warn-soft) 20%, var(--ok-soft) 20% 80%, var(--warn-soft) 80%)' }}>
            <i style={{ width: 4, left: `calc(${(pos ?? 0.5) * 100}% - 2px)`, right: 'auto', background: out ? 'var(--crit)' : m.color, borderRadius: 2 }} />
          </div>
          <div className="row between small faint" style={{ marginTop: 4 }}><span>{fmt(metric, lo, units)}</span><span>safe range</span><span>{fmt(metric, hi, units)}</span></div>
        </div>
      )}
      {stats && <div className="small faint" style={{ marginTop: 8 }}>24 h · min <b className="mono">{fmt(metric, stats.min, units)}</b> · max <b className="mono">{fmt(metric, stats.max, units)}</b></div>}
    </div>
  );
}

/* ---------- drawer & modal ---------- */
function useEsc(onClose, open) {
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);
}
export function Drawer({ open, onClose, title, sub, children }) {
  useEsc(onClose, open);
  if (!open) return null;
  return (
    <>
      <div className="overlay" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer__head">
          <div><h2>{title}</h2>{sub && <div className="card__sub">{sub}</div>}</div>
          <button className="btn icon ghost" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="drawer__body">{children}</div>
      </aside>
    </>
  );
}
export function Modal({ open, onClose, title, children, footer }) {
  useEsc(onClose, open);
  if (!open) return null;
  return (
    <>
      <div className="overlay" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="card__head" style={{ padding: 20 }}><h2>{title}</h2><button className="btn icon ghost sm" onClick={onClose} aria-label="Close"><X size={17} /></button></div>
        <div className="card__body">{children}</div>
        {footer && <div className="row" style={{ justifyContent: 'flex-end', padding: '0 18px 18px' }}>{footer}</div>}
      </div>
    </>
  );
}
